import type { CallExport } from './calls-csv.js';
import { ApiError, RequestTimeout } from './errors.js';

// Re-exported so every screen keeps importing its errors from one place.
export { ApiError, RequestTimeout };

/**
 * The dashboard's only route to the server.
 *
 * Two things this file exists to make structural rather than remembered.
 *
 * **The establishment goes in a header, on every request.** A person can belong
 * to several salons, and getting this wrong shows one salon's calls under
 * another's name. It is set here, once, rather than at each call site.
 *
 * **Nothing in the browser is a secret.** This bundle is readable by anyone who
 * opens the page, so the token it holds is a bearer credential for one person
 * and the server re-checks every permission on every request. The dashboard
 * hides controls a role cannot use, and that is presentation — an attacker does
 * not use our UI.
 */

export interface ApiSession {
  readonly baseUrl: string;
  readonly token: string;
  readonly tenantId: string;
}

interface ErrorBody {
  readonly error?: { readonly code?: string; readonly message?: string };
}

/**
 * Performs one request.
 *
 * Every failure becomes an `ApiError` carrying the server's stable code, so
 * screens branch on `code` rather than on a message. A message is for a person
 * to read; matching on one is how a copy-editing change breaks error handling.
 */
/**
 * `headers` is a plain record rather than `HeadersInit`.
 *
 * `HeadersInit` is also an array of pairs and a `Headers` instance, and
 * spreading either of those into an object literal produces indices and
 * nothing else — silently, as a request with no headers at all. Narrowing the
 * type makes the spread below correct by construction.
 */
interface RequestOptions {
  readonly method?: string;
  readonly headers?: Readonly<Record<string, string>>;
  readonly body?: string;
}

async function request<T>(
  session: ApiSession,
  path: string,
  init: RequestOptions = {},
): Promise<T> {
  return await answered((init.method ?? 'GET') !== 'GET', async (signal) => {
    const response = await fetch(`${session.baseUrl}${path}`, {
      ...init,
      signal,
      headers: {
        ...init.headers,
        accept: 'application/json',
        authorization: `Bearer ${session.token}`,
        // Which establishment this request is for. Never inferred from the URL.
        //
        // Omitted entirely when empty, rather than sent blank: the API
        // validates the header's shape, so a blank one is a malformed request
        // rather than "no establishment". The only call that legitimately has
        // none is the one that asks which establishments you belong to.
        ...(session.tenantId === '' ? {} : { 'x-gsi-tenant': session.tenantId }),
        ...(init.body === undefined ? {} : { 'content-type': 'application/json' }),
      },
    });

    const requestId = response.headers.get('x-request-id');

    if (!response.ok) {
      let body: ErrorBody = {};
      try {
        body = (await response.json()) as ErrorBody;
      } catch {
        // A non-JSON error body is a proxy or a gateway answering, not us.
      }
      throw new ApiError(
        response.status,
        body.error?.code ?? 'unknown',
        body.error?.message ?? 'Une erreur est survenue.',
        requestId,
      );
    }

    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  });
}

/**
 * How long the dashboard waits for an answer.
 *
 * Without a limit, a request the network loses is a skeleton that never ends,
 * or a button that says « Enregistrement… » for the rest of the afternoon: the
 * browser's own limit is minutes long, and on a phone in a salon whose wifi
 * drops, minutes is what it takes. With one, the screen says the server did not
 * answer and offers to ask again.
 *
 * A read gives up sooner than a write. A read that takes twenty seconds is
 * broken, whatever the reason, and asking again costs nothing. A write may be
 * waiting on somebody else's server — the booking software being verified, a
 * number being ordered from the operator, Stripe — and abandoning it early
 * would turn a slow success into an unknown outcome.
 */
const READ_WITHIN_MS = 20_000;
const WRITE_WITHIN_MS = 90_000;

/**
 * Runs one exchange with the server — the request and the reading of its body
 * — and abandons it once its time is up, as a `RequestTimeout`.
 *
 * A timer and an `AbortController` rather than `AbortSignal.timeout`, which
 * Safari only has from version 16: on an older iPhone, every request would
 * fail before it was sent.
 */
async function answered<T>(
  write: boolean,
  exchange: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(
    () => {
      controller.abort();
    },
    write ? WRITE_WITHIN_MS : READ_WITHIN_MS,
  );
  try {
    return await exchange(controller.signal);
  } catch (cause: unknown) {
    // However the abort surfaced — from `fetch`, or from reading the body —
    // it is reported as what it was.
    if (controller.signal.aborted) throw new RequestTimeout(write);
    throw cause;
  } finally {
    clearTimeout(timer);
  }
}

/* ------------------------------------------------------------------ types -- */

export interface CallListItem {
  readonly callId: string;
  readonly startedAt: string;
  readonly durationSeconds: number | null;
  readonly direction: string;
  readonly origin: string;
  readonly intent: string | null;
  readonly outcome: string | null;
  readonly taskCompleted: boolean | null;
  readonly transferredToHuman: boolean;
  readonly customerId: string | null;
  readonly bookingId: string | null;
}

export interface CallListPage {
  readonly items: readonly CallListItem[];
  readonly nextCursor: string | null;
}

export interface CallTimelineEntry {
  readonly sequence: number;
  readonly kind: 'transition' | 'tool';
  readonly name: string;
  readonly outcome: string | null;
  readonly occurredAt: string;
  readonly payload: unknown;
}

export interface CallDetail {
  readonly call: CallListItem;
  readonly timeline: readonly CallTimelineEntry[];
  readonly incompletionReason: string | null;
  readonly clarificationCount: number | null;
  readonly lowestConfidence: string | null;
}

/**
 * What this establishment's offer includes of the statistics.
 *
 * The server's answer, from the resolved entitlements; the screens read it
 * rather than guessing from a plan name, so they cannot disagree with what the
 * server serves.
 */
export interface IncludedStatistics {
  readonly bookingValue: boolean;
  readonly reasonsForCalling: boolean;
  readonly export: boolean;
}

export interface RoiSummary {
  readonly from: string;
  readonly to: string;
  readonly callsProcessed: number;
  readonly callsRecovered: number;
  readonly bookingsCreated: number;
  readonly tasksCompleted: number;
  readonly tasksAttempted: number;
  readonly transfersToHuman: number;
  /** Null when the offer does not include the value of appointments. */
  readonly associatedBookingValueMinor: number | null;
  readonly capturedPaymentValueMinor: number | null;
  readonly estimatedTimeSavedSeconds: number;
  readonly currency: string;
  readonly included: IncludedStatistics;
}

export interface ActivityDay {
  /** `YYYY-MM-DD` in the establishment's own calendar, not in UTC. */
  readonly date: string;
  readonly calls: number;
  readonly answered: number;
  readonly bookings: number;
  readonly transfers: number;
}

export interface ActivitySummary {
  readonly from: string;
  readonly to: string;
  readonly timezone: string;
  readonly days: readonly ActivityDay[];
  readonly outcomes: readonly { readonly outcome: string; readonly calls: number }[];
  /** Null when the offer does not include the breakdown by reason for calling. */
  readonly intents:
    | readonly {
        readonly intent: string;
        readonly attempted: number;
        readonly completed: number;
      }[]
    | null;
  readonly included: IncludedStatistics;
}

export interface LiveCallStep {
  readonly sequence: number;
  readonly kind: 'transition' | 'tool';
  readonly name: string;
  readonly occurredAt: string;
}

/**
 * A call happening right now.
 *
 * Note what is not here: no caller identifier, no payload, no transcript. The
 * server does not return them, so no screen can leak one by forgetting to
 * leave it out.
 */
export interface LiveCall {
  readonly callId: string;
  readonly startedAt: string;
  readonly state: string;
  readonly origin: string;
  readonly intent: string | null;
  readonly knownCustomer: boolean;
  readonly steps: readonly LiveCallStep[];
}

export interface AssistantSettings {
  readonly assistantName: string;
  readonly language: string;
  readonly greeting: string | null;
  readonly businessContext: string | null;
  readonly voiceId: string | null;
  readonly recordingEnabled: boolean;
  readonly transcriptRetentionDays: number;
  readonly maxCallSeconds: number;
  readonly allowHumanTransfer: boolean;
  /** The whole price paid by a link during the call, the slot held meanwhile. */
  readonly paymentAtBooking: boolean;
  readonly updatedAt: string;
}

/**
 * What a settings form may send.
 *
 * There is no prompt field, and adding one here would not help: the server's
 * schema rejects unknown keys. This type exists to make that visible at the
 * call site rather than only at the boundary.
 */
export interface AssistantSettingsPatch {
  readonly assistantName?: string;
  readonly language?: string;
  readonly greeting?: string | null;
  readonly businessContext?: string | null;
  readonly recordingEnabled?: boolean;
  readonly transcriptRetentionDays?: number;
  readonly maxCallSeconds?: number;
  readonly allowHumanTransfer?: boolean;
  readonly paymentAtBooking?: boolean;
}

export interface PhoneLine {
  readonly phoneNumberId: string;
  readonly e164: string;
  readonly provider: string;
  readonly purpose: string;
  readonly fallbackE164: string | null;
  readonly isActive: boolean;
  readonly routingActions: readonly string[];
}

export interface OpeningHoursEntry {
  /** 0 is Sunday. */
  readonly weekday: number;
  readonly opensAtMinutes: number;
  readonly closesAtMinutes: number;
}

export interface OpeningHoursView {
  readonly locationId: string;
  readonly locationName: string;
  readonly timezone: string;
  readonly hours: readonly OpeningHoursEntry[];
  /** True when these are the week we assume, not the one the salon entered. */
  readonly isDefault: boolean;
}

/**
 * What the assistant can offer, read through the call's own port.
 *
 * Not the same read as the **Prestations et équipe** screen. That one shows
 * what the establishment owns; this one is scoped to the active booking
 * connection and sees only what a caller could actually be offered. The gap
 * between the two is the diagnosis when an assistant says it has nothing while
 * the screen shows six.
 */
export interface AssistantReadiness {
  readonly connector: string | null;
  readonly servicesOffered: number;
  readonly servicesOwned: number;
  readonly staffOffered: number;
  readonly staffOwned: number;
}

export interface PhoneHealth {
  readonly lines: readonly PhoneLine[];
  readonly callsLastDay: number;
  readonly answeredLastDay: number;
  readonly routingFailuresLastDay: number;
  readonly lastCallAt: string | null;
  /**
   * Optional, because a browser can hold a newer bundle than the API serving
   * it — a cached page, or a Worker rolled back on its own. Destructuring an
   * absent object here would blank the page somebody opens precisely when
   * something is already wrong, which is a poor trade for one panel.
   */
  readonly assistant?: AssistantReadiness;
}

/* ------------------------------------------------------------------- calls -- */

export async function listCalls(
  session: ApiSession,
  options: { readonly cursor?: string | null; readonly outcome?: string | null } = {},
): Promise<CallListPage> {
  const params = new URLSearchParams();
  if (options.cursor != null && options.cursor !== '') params.set('cursor', options.cursor);
  if (options.outcome != null && options.outcome !== '') params.set('outcome', options.outcome);

  const query = params.toString();
  return await request<CallListPage>(session, `/v1/calls${query === '' ? '' : `?${query}`}`);
}

export async function getCall(session: ApiSession, callId: string): Promise<CallDetail> {
  return await request<CallDetail>(session, `/v1/calls/${encodeURIComponent(callId)}`);
}

/** Calls in progress. Polled; see the note on the endpoint for why not a stream. */
export async function getLiveCalls(session: ApiSession): Promise<readonly LiveCall[]> {
  const body = await request<{ calls: readonly LiveCall[] }>(session, '/v1/calls/live');
  return body.calls;
}

export async function getAssistantSettings(session: ApiSession): Promise<AssistantSettings> {
  return await request<AssistantSettings>(session, '/v1/settings/assistant');
}

export async function saveAssistantSettings(
  session: ApiSession,
  patch: AssistantSettingsPatch,
): Promise<AssistantSettings> {
  return await request<AssistantSettings>(session, '/v1/settings/assistant', {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

export async function getOpeningHours(session: ApiSession): Promise<OpeningHoursView> {
  return await request<OpeningHoursView>(session, '/v1/settings/hours');
}

export async function saveOpeningHours(
  session: ApiSession,
  hours: readonly OpeningHoursEntry[],
): Promise<{ readonly hours: readonly OpeningHoursEntry[]; readonly isDefault: boolean }> {
  return await request(session, '/v1/settings/hours', {
    method: 'PUT',
    body: JSON.stringify({ hours }),
  });
}

/* ------------------------------------------------------------- catalogue -- */

/**
 * A prestation, as the salon edits it.
 *
 * `aliases` are the phrasings a caller may use — the matcher treats them as
 * other names for the same thing, which is what makes "je voudrais un
 * dégradé" resolve to "Coupe homme".
 */
export interface CatalogueService {
  readonly id: string;
  readonly name: string;
  readonly aliases: readonly string[];
  readonly description: string | null;
  readonly durationMinutes: number;
  readonly priceMinor: number | null;
  readonly priceIsMinimum: boolean;
  readonly currency: string;
  readonly isActive: boolean;
  readonly staffIds: readonly string[];
}

export interface CatalogueStaff {
  readonly id: string;
  readonly displayName: string;
  readonly isActive: boolean;
  readonly acceptsBookings: boolean;
}

export interface CatalogueView {
  readonly services: readonly CatalogueService[];
  readonly staff: readonly CatalogueStaff[];
}

export interface ServiceDraft {
  readonly name: string;
  readonly aliases: readonly string[];
  readonly description: string | null;
  readonly durationMinutes: number;
  readonly priceMinor: number | null;
  readonly priceIsMinimum: boolean;
  readonly isActive: boolean;
  readonly staffIds: readonly string[];
}

export interface StaffDraft {
  readonly displayName: string;
  readonly isActive: boolean;
  readonly acceptsBookings: boolean;
}

export async function getCatalogue(session: ApiSession): Promise<CatalogueView> {
  return await request<CatalogueView>(session, '/v1/catalogue');
}

export async function saveService(
  session: ApiSession,
  draft: ServiceDraft,
  id?: string,
): Promise<CatalogueService> {
  return await request<CatalogueService>(
    session,
    id === undefined
      ? '/v1/catalogue/services'
      : `/v1/catalogue/services/${encodeURIComponent(id)}`,
    { method: id === undefined ? 'POST' : 'PUT', body: JSON.stringify(draft) },
  );
}

export async function saveStaff(
  session: ApiSession,
  draft: StaffDraft,
  id?: string,
): Promise<CatalogueStaff> {
  return await request<CatalogueStaff>(
    session,
    id === undefined ? '/v1/catalogue/staff' : `/v1/catalogue/staff/${encodeURIComponent(id)}`,
    { method: id === undefined ? 'POST' : 'PUT', body: JSON.stringify(draft) },
  );
}

/* ----------------------------------------------------------------- audit -- */

/**
 * One recorded action.
 *
 * `actorName` is a display name, never an address: the audit screen is not a
 * route to the staff directory. `metadata` carries facts about the change —
 * which role, which fields — and by construction never contact details.
 */
export interface AuditEntry {
  readonly id: string;
  readonly occurredAt: string;
  readonly action: string;
  readonly actorType: string;
  readonly actorName: string | null;
  readonly resourceType: string | null;
  readonly resourceId: string | null;
  readonly metadata: Readonly<Record<string, unknown>>;
}

export interface AuditPage {
  readonly entries: readonly AuditEntry[];
  readonly nextCursor: string | null;
  /** The actions this establishment has recorded, for the filter. */
  readonly actions: readonly string[];
}

export async function getAuditLog(
  session: ApiSession,
  options: { readonly action?: string | null; readonly cursor?: string | null } = {},
): Promise<AuditPage> {
  const params = new URLSearchParams();
  if (options.action != null && options.action !== '') params.set('action', options.action);
  if (options.cursor != null && options.cursor !== '') params.set('cursor', options.cursor);
  const query = params.toString();
  return await request<AuditPage>(session, `/v1/audit${query === '' ? '' : `?${query}`}`);
}

/* --------------------------------------------------------------- billing -- */

/**
 * The subscription, as the server resolved it.
 *
 * `features` and `limits` are the **effective** values for this establishment,
 * overrides and grants already applied. The screen renders them; it never
 * looks a plan up in a table of its own, which is how a dashboard ends up
 * telling a customer they do not have a feature they are using.
 *
 * `plan` is a label to print. Branching on it is forbidden and linted against.
 */
export interface SubscriptionView {
  readonly plan: {
    readonly planCode: string;
    readonly planName: string;
    readonly versionNumber: number;
    readonly billingInterval: string;
  } | null;
  readonly subscription: {
    readonly status: string;
    readonly billingInterval: string;
    readonly currentPeriodEnd: string | null;
    readonly cancelAtPeriodEnd: boolean;
    readonly trialEndsAt: string | null;
    /** Whether a Stripe portal session can be opened. Never the identifier. */
    readonly manageable: boolean;
  } | null;
  readonly period: {
    readonly startsAt: string;
    readonly endsAt: string;
    readonly includedMinutes: number | null;
  } | null;
  readonly usage: {
    readonly includedMinutes: number | null;
    readonly consumedMinutes: number;
    readonly remainingMinutes: number | null;
    readonly overageMinutes: number;
    readonly overageCostMinor: number;
    readonly currency: string;
    readonly usageRatio: number | null;
  } | null;
  readonly features: Readonly<Record<string, boolean>>;
  readonly limits: Readonly<Record<string, number | null>>;
  /**
   * Where each quota came from.
   *
   * `default` means nothing set it. The resolved value is then `0`, because
   * enforcement fails closed — but presenting that as a quota would tell a
   * salon their telephone allows no calls. The screen shows it as unstated.
   */
  readonly limitSources: Readonly<Record<string, string>>;
}

export async function getSubscription(session: ApiSession): Promise<SubscriptionView> {
  return await request<SubscriptionView>(session, '/v1/billing/subscription');
}

/** A short-lived Stripe URL, made per request and never stored. */
export async function openBillingPortal(
  session: ApiSession,
  returnUrl: string,
): Promise<{ readonly url: string }> {
  return await request<{ url: string }>(session, '/v1/billing/portal', {
    method: 'POST',
    body: JSON.stringify({ returnUrl }),
  });
}

export type OfferInterval =
  | {
      readonly interval: 'monthly' | 'annual';
      readonly available: true;
      readonly currency: string;
      /** Excluding VAT, every later invoice. */
      readonly recurringMinor: number;
      /** Excluding VAT, the first invoice: the recurring price and any setup fee. */
      readonly firstInvoiceMinor: number;
      readonly setupMinor: number;
      /** True when the plan has a setup fee and this commitment waives it. */
      readonly setupWaived: boolean;
      /** Including this establishment's VAT; absent when no rate can be decided. */
      readonly recurringWithVatMinor?: number;
      readonly firstInvoiceWithVatMinor?: number;
    }
  | {
      readonly interval: 'monthly' | 'annual';
      readonly available: false;
      readonly reason: 'interval_unavailable' | 'not_in_stripe';
    };

export interface OfferView {
  readonly planCode: string;
  readonly planName: string;
  readonly description: string | null;
  readonly versionNumber: number;
  /** Terms agreed with this establishment; when there is one, it is the only offer. */
  readonly contract: boolean;
  readonly limits: Readonly<Record<string, number | null>>;
  readonly features: readonly string[];
  readonly intervals: readonly OfferInterval[];
}

export interface OffersView {
  /** A subscription is already running: nothing here is for sale to them. */
  readonly subscribed: boolean;
  /** Whether this person may pay: the owner, with a second factor. */
  readonly canSubscribe: boolean;
  /** Whether this deployment takes payments online at all. */
  readonly online: boolean;
  readonly address: { readonly postalCode: string | null; readonly city: string | null } | null;
  /** The rate for this establishment's address, or null when none can be decided. */
  readonly vat: {
    readonly label: string;
    readonly percent: number;
    readonly legalMention: string | null;
  } | null;
  readonly offers: readonly OfferView[];
}

export async function getOffers(session: ApiSession): Promise<OffersView> {
  return await request<OffersView>(session, '/v1/billing/offers');
}

/** Stripe's payment page for an offer: the browser is sent there. */
export async function startCheckout(
  session: ApiSession,
  planCode: string,
  interval: 'monthly' | 'annual',
): Promise<{ readonly url: string }> {
  return await request<{ url: string }>(session, '/v1/billing/checkout', {
    method: 'POST',
    body: JSON.stringify({ planCode, interval }),
  });
}

/* --------------------------------------------------------- establishment -- */

/**
 * Where the salon is, and which clock its day runs on.
 *
 * Every other screen depends on this row existing: without it the opening
 * hours answer 404, the diary has no zone to work availability out from, and
 * no appointment can be written at all.
 */
export interface Establishment {
  readonly locationId: string;
  readonly name: string;
  readonly timezone: string;
  readonly addressLine: string | null;
  readonly city: string | null;
  readonly postalCode: string | null;
  readonly countryCode: string;
}

export type EstablishmentDraft = Omit<Establishment, 'locationId'>;

/** `null` when the tenant has none yet — which the screen exists to fix. */
export async function getEstablishment(session: ApiSession): Promise<Establishment | null> {
  const body = await request<{ location: Establishment | null }>(session, '/v1/settings/location');
  return body.location;
}

export async function saveEstablishment(
  session: ApiSession,
  draft: EstablishmentDraft,
): Promise<Establishment> {
  return await request<Establishment>(session, '/v1/settings/location', {
    method: 'PUT',
    body: JSON.stringify(draft),
  });
}

/* -------------------------------------------------------------- bookings -- */

/**
 * One appointment, as the salon may see it.
 *
 * The caller's name when they gave one, and never their number: the same rule
 * the calls list follows, because a page of contact details at a time is what
 * turns one stolen session into a mailing list.
 */
export interface BookingItem {
  readonly id: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly status: string;
  readonly source: string;
  readonly serviceName: string;
  readonly staffName: string | null;
  readonly customerName: string | null;
  /** The establishment's own zone, so a reader elsewhere sees the right hour. */
  readonly timezone: string;
  readonly callId: string | null;
  readonly priceMinor: number | null;
  readonly currency: string;
  readonly cancelledAt: string | null;
  /**
   * The payment taken at booking, when the salon asks for one. `awaiting`
   * carries the time the slot is held until.
   */
  readonly payment: {
    readonly state: 'awaiting' | 'paid' | 'released' | 'unpaid' | 'cancelled';
    readonly holdUntil: string | null;
    readonly amountMinor: number;
  } | null;
}

export interface BookingPage {
  readonly items: readonly BookingItem[];
  readonly nextCursor: string | null;
  /** The whole window by status, including what a filter is hiding. */
  readonly counts: Readonly<Record<string, number>>;
  readonly timezone: string;
}

export async function getBookings(
  session: ApiSession,
  options: {
    readonly from: Date;
    readonly to: Date;
    readonly status?: string | null;
    readonly cursor?: string | null;
    readonly limit?: number;
  },
): Promise<BookingPage> {
  const params = new URLSearchParams({
    from: options.from.toISOString(),
    to: options.to.toISOString(),
  });
  if (options.status != null && options.status !== '') params.set('status', options.status);
  if (options.cursor != null && options.cursor !== '') params.set('cursor', options.cursor);
  if (options.limit !== undefined) params.set('limit', String(options.limit));

  return await request<BookingPage>(session, `/v1/bookings?${params.toString()}`);
}

/* ------------------------------------------------------------- payments -- */

/**
 * The salon's own Stripe account, as the Paiements card shows it.
 *
 * `available` is whether this service can connect anybody at all; the
 * connection is the salon's own, never an account we opened.
 */
export interface PaymentConnectionView {
  readonly available: boolean;
  readonly connection: {
    readonly providerAccountId: string;
    readonly status: string;
    readonly chargesEnabled: boolean;
    readonly payoutsEnabled: boolean;
    readonly detailsSubmitted: boolean;
    readonly ownAccount: boolean;
    readonly livemode: boolean | null;
    readonly connectedAt: string | null;
  } | null;
}

export async function getPaymentConnection(session: ApiSession): Promise<PaymentConnectionView> {
  return await request<PaymentConnectionView>(session, '/v1/payments/connection');
}

/** Stripe's page, where the owner authorises GSI Voice. The owner's alone. */
export async function startPaymentConnection(
  session: ApiSession,
): Promise<{ readonly url: string }> {
  return await request(session, '/v1/payments/connection/start', { method: 'POST' });
}

/** Finishes with what Stripe sent the owner back with. */
export async function completePaymentConnection(
  session: ApiSession,
  input: { readonly code: string; readonly state: string },
): Promise<Pick<PaymentConnectionView, 'connection'>> {
  return await request(session, '/v1/payments/connection/complete', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function refreshPaymentConnection(
  session: ApiSession,
): Promise<Pick<PaymentConnectionView, 'connection'>> {
  return await request(session, '/v1/payments/connection/refresh', { method: 'POST' });
}

export async function disconnectPayment(session: ApiSession): Promise<void> {
  await request(session, '/v1/payments/connection', { method: 'DELETE' });
}

/* ------------------------------------------------------------- knowledge -- */

/**
 * One answer the assistant can give.
 *
 * `tags` are other ways a caller might ask the same thing. The matcher treats
 * them as aliases of the question, which is why the screen calls them
 * "formulations" rather than "mots-clés": they are phrasings, not keywords.
 */
export interface KnowledgeEntry {
  readonly id: string;
  readonly question: string;
  readonly answer: string;
  readonly tags: readonly string[];
  readonly isActive: boolean;
  readonly updatedAt: string;
}

export interface KnowledgeView {
  readonly entries: readonly KnowledgeEntry[];
  /** The server's bounds, so the form can state them before refusing anything. */
  readonly limit: number;
  readonly maxQuestionLength: number;
  readonly maxAnswerLength: number;
}

export interface KnowledgeDraft {
  readonly question: string;
  readonly answer: string;
  readonly tags: readonly string[];
}

export async function getKnowledge(session: ApiSession): Promise<KnowledgeView> {
  return await request<KnowledgeView>(session, '/v1/knowledge');
}

export async function createKnowledgeEntry(
  session: ApiSession,
  draft: KnowledgeDraft,
): Promise<KnowledgeEntry> {
  return await request<KnowledgeEntry>(session, '/v1/knowledge', {
    method: 'POST',
    body: JSON.stringify(draft),
  });
}

/** Sends only what changed; an absent field leaves the stored one alone. */
export async function updateKnowledgeEntry(
  session: ApiSession,
  id: string,
  patch: Partial<KnowledgeDraft> & { readonly isActive?: boolean },
): Promise<KnowledgeEntry> {
  return await request<KnowledgeEntry>(session, `/v1/knowledge/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

export async function deleteKnowledgeEntry(session: ApiSession, id: string): Promise<void> {
  // The endpoint answers 204, so there is no body to type. `request` returns
  // `undefined` for a 204 and the result is deliberately discarded.
  await request<never>(session, `/v1/knowledge/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function getPhoneHealth(session: ApiSession): Promise<PhoneHealth> {
  return await request<PhoneHealth>(session, '/v1/telephony/health');
}

/**
 * Where a handed-over call on this line rings. Null stops transferring.
 *
 * The owner's alone, with a second factor; every owner is told by e-mail.
 */
export async function setTransferNumber(
  session: ApiSession,
  phoneNumberId: string,
  transferE164: string | null,
): Promise<{ readonly transferE164: string | null; readonly ownersNotified: number }> {
  return await request(
    session,
    `/v1/telephony/lines/${encodeURIComponent(phoneNumberId)}/transfer`,
    {
      method: 'PUT',
      body: JSON.stringify({ transferE164 }),
    },
  );
}

export async function getRoi(
  session: ApiSession,
  window: { readonly from: Date; readonly to: Date },
): Promise<RoiSummary> {
  const params = new URLSearchParams({
    from: window.from.toISOString(),
    to: window.to.toISOString(),
  });
  return await request<RoiSummary>(session, `/v1/analytics/roi?${params.toString()}`);
}

export type { CallExport, CallExportRow } from './calls-csv.js';

/** Every call of a window, for the CSV the statistics screen builds. */
export async function getCallExport(
  session: ApiSession,
  window: { readonly from: Date; readonly to: Date },
): Promise<CallExport> {
  const params = new URLSearchParams({
    from: window.from.toISOString(),
    to: window.to.toISOString(),
  });
  return await request<CallExport>(session, `/v1/analytics/export?${params.toString()}`);
}

export interface Membership {
  readonly tenantId: string;
  /**
   * What the salon is called.
   *
   * The picker showed a UUID before this existed, which is not a choice
   * anybody can make: the cost of picking wrong is looking at another salon's
   * calls. It is a label — nothing in the client branches on it, and the
   * establishment sent to the server is still the identifier.
   */
  readonly name: string;
  readonly role: string;
}

/**
 * Activity per day, for the chart and the breakdowns.
 *
 * The timezone comes from the browser rather than from a setting, because the
 * browser is right: the person reading this is in the salon. Without it the
 * server buckets by UTC, and a Guadeloupe evening lands on the next day's bar.
 */
export async function getActivity(
  session: ApiSession,
  window: { readonly from: Date; readonly to: Date },
): Promise<ActivitySummary> {
  const params = new URLSearchParams({
    from: window.from.toISOString(),
    to: window.to.toISOString(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  return await request<ActivitySummary>(session, `/v1/analytics/activity?${params.toString()}`);
}

export interface Identity {
  /** Null for somebody authenticated who has no account on this deployment. */
  readonly principal: null | {
    readonly kind: string;
    readonly userId?: string;
    readonly tenantId?: string;
    readonly role?: string;
    readonly mfaSatisfied?: boolean;
  };
  readonly memberships: readonly Membership[];
}

/**
 * Who you are, and which establishments you belong to.
 *
 * The one call with no establishment header: it is what tells you which ones
 * exist for you. Everything after it carries one.
 */
export async function getIdentity(session: Omit<ApiSession, 'tenantId'>): Promise<Identity> {
  return await request<Identity>({ ...session, tenantId: '' }, '/v1/me');
}

/* ------------------------------------------------------ signing up -- */

export interface EstablishmentRequest {
  readonly name: string;
  readonly addressLine?: string;
  readonly postalCode: string;
  readonly city: string;
}

export interface CreatedEstablishment {
  readonly tenantId: string;
  readonly name: string;
  /** Decided by the server from the postal code, never chosen in the form. */
  readonly timezone: string;
  readonly territory: string;
}

/**
 * Creates the establishment of the person signed in, who becomes its owner.
 *
 * Sent without an establishment header: there is none yet, and for somebody
 * who already works in one, this is not a request about that one.
 */
export async function createEstablishment(
  session: Omit<ApiSession, 'tenantId'>,
  body: EstablishmentRequest,
): Promise<CreatedEstablishment> {
  return await request<CreatedEstablishment>(
    { ...session, tenantId: '' },
    '/v1/signup/establishment',
    { method: 'POST', body: JSON.stringify(body) },
  );
}

/* ----------------------------------------------------------- signing in -- */

/**
 * Signing in, from the browser's side.
 *
 * Three calls, and what they deliberately do **not** do is the important part:
 * nothing here writes a token to `localStorage`, `sessionStorage` or a cookie
 * this code can read. The access token lives in a React state variable and
 * dies with the tab. What survives a reload is an HttpOnly cookie set by the
 * server, which this file cannot see and a cross-site script cannot steal.
 */

export interface AuthConfig {
  /** Whether this deployment has a hosted sign-in to send people to. */
  readonly hostedLogin: boolean;
}

export interface Session {
  readonly accessToken: string;
  /** When the token stops working, so the page can renew it before it does. */
  readonly expiresAt: string;
}

/**
 * Which sign-in this deployment offers.
 *
 * A failure throws rather than answering "none". It used to answer "none", and
 * "none" is the screen for pasting a developer's token: a salon whose network
 * dropped at the wrong moment was asked for a JWT.
 */
export async function getAuthConfig(baseUrl: string): Promise<AuthConfig> {
  return await answered(false, async (signal) => {
    const response = await fetch(`${baseUrl}/v1/auth/config`, {
      headers: { accept: 'application/json' },
      signal,
    });
    if (!response.ok) {
      throw new ApiError(
        response.status,
        'auth.unavailable',
        'La connexion est momentanément indisponible.',
        response.headers.get('x-request-id'),
      );
    }
    return (await response.json()) as AuthConfig;
  });
}

/**
 * Renews the access token from the session cookie.
 *
 * Returns `null` for "you are not signed in", which is an ordinary answer and
 * not an error: it is what every first visit gets. A genuine failure — the
 * server unreachable, a misconfiguration — still throws.
 */
export async function renewSession(baseUrl: string): Promise<Session | null> {
  // A write: the server rotates the refresh token behind this call.
  return await answered(true, async (signal) => {
    const response = await fetch(`${baseUrl}/v1/auth/token`, {
      method: 'POST',
      headers: { accept: 'application/json' },
      // Same origin, and stated rather than assumed: the cookie is the whole
      // input to this call.
      credentials: 'same-origin',
      signal,
    });

    // 401 is "you are not signed in", which is what every first visit gets.
    if (response.status === 401) return null;

    // 503 is something else entirely, and the difference is the point: the
    // identity provider is unreachable, so the session is probably fine and
    // the server has deliberately kept it. Showing a sign-in screen here would
    // be a lie, and clicking its button would fail too.
    if (!response.ok) {
      throw new ApiError(
        response.status,
        response.status === 503 ? 'auth.provider_unavailable' : 'auth.unavailable',
        response.status === 503
          ? 'Le service d’identité ne répond pas. Votre session est conservée ; réessayez dans un instant.'
          : 'La connexion est momentanément indisponible.',
        response.headers.get('x-request-id'),
      );
    }
    return (await response.json()) as Session;
  });
}

/**
 * Ends the session here, and reports where to end it at the provider.
 *
 * The cookie is cleared by the server before this returns, so a failure to
 * reach the provider afterwards still leaves this browser signed out.
 */
export async function endSession(baseUrl: string, token: string | null): Promise<string | null> {
  return await answered(true, async (signal) => {
    const response = await fetch(`${baseUrl}/v1/auth/logout`, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        ...(token === null ? {} : { authorization: `Bearer ${token}` }),
      },
      credentials: 'same-origin',
      signal,
    });
    if (!response.ok) return null;
    const body = (await response.json()) as { readonly logoutUrl?: string | null };
    return body.logoutUrl ?? null;
  });
}

export interface RevokedSessions {
  /** When the cut-off was set. Everything issued before it is refused. */
  readonly revokedFrom: string | null;
  /** Always false today, and said rather than implied. See the screen. */
  readonly providerSessionEnded: boolean;
}

/**
 * Ends every session this account has, on every device.
 *
 * The half we control, done immediately: any token issued before this instant
 * is refused on its next request. It does **not** revoke the refresh token
 * held at the identity provider, which is why the response says so and the
 * screen repeats it.
 */
export async function revokeAllSessions(
  baseUrl: string,
  token: string | null,
  tenantId: string | null,
): Promise<RevokedSessions> {
  return await answered(true, async (signal) => {
    const response = await fetch(`${baseUrl}/v1/me/sessions/revoke-all`, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        ...(token === null ? {} : { authorization: `Bearer ${token}` }),
        ...(tenantId === null ? {} : { 'x-gsi-tenant': tenantId }),
      },
      credentials: 'same-origin',
      signal,
    });
    if (!response.ok) {
      // Written here, in French, for the screen to show as it is: the server's
      // own message is for an operator.
      throw new ApiError(
        response.status,
        response.status === 403 ? 'authz.denied' : 'sessions.not_revoked',
        response.status === 403
          ? 'Cette action demande une connexion avec second facteur.'
          : 'Les sessions n’ont pas pu être terminées.',
        response.headers.get('x-request-id'),
      );
    }
    return (await response.json()) as RevokedSessions;
  });
}

/* ------------------------------------------------------------ onboarding -- */

export interface ConnectorCapabilities {
  readonly realtimeAvailability: boolean;
  readonly holdSlot: boolean;
  readonly pendingBooking: boolean;
  readonly cancel: boolean;
  readonly reschedule: boolean;
  readonly payments: boolean;
  readonly employees: boolean;
  readonly multiLocation: boolean;
  readonly idempotentWrites: boolean;
}

/**
 * A saved connection, as the server is willing to describe it.
 *
 * There is no `credentials` field, and there is not meant to be one. The server
 * never returns them — not masked, not truncated — so the wizard asks for them
 * again rather than pretending to show what is stored.
 */
export interface BookingConnection {
  readonly connectionId: string;
  readonly provider: string;
  readonly displayName: string;
  readonly baseUrl: string;
  readonly status: 'pending' | 'active' | 'error' | 'disabled';
  readonly lastVerifiedAt: string | null;
  readonly lastError: string | null;
  readonly capabilities: ConnectorCapabilities;
}

export interface SavedConnection {
  readonly connectionId: string | null;
  readonly provider: string;
  readonly status: string;
  readonly verified: boolean;
  readonly message: string | null;
  readonly capabilities: ConnectorCapabilities;
}

export interface PlanPreview {
  readonly summary: {
    readonly create: number;
    readonly update: number;
    readonly unchanged: number;
    readonly deactivate: number;
  };
  readonly changes: readonly { readonly kind: string; readonly name: string }[];
  readonly truncated: boolean;
}

export interface ImportPreview {
  readonly services: PlanPreview;
  readonly staff: PlanPreview;
}

export interface ImportResult {
  readonly applied: {
    readonly servicesCreated: number;
    readonly servicesUpdated: number;
    readonly servicesDeactivated: number;
    readonly staffCreated: number;
    readonly staffUpdated: number;
    readonly staffDeactivated: number;
    readonly staffServiceLinks: number;
  };
  readonly readiness: { readonly ready: boolean; readonly blockers: readonly string[] };
}

export async function getConnection(session: ApiSession): Promise<BookingConnection | null> {
  const body = await request<{ connection: BookingConnection | null }>(
    session,
    '/v1/onboarding/connection',
  );
  return body.connection;
}

export async function saveConnection(
  session: ApiSession,
  input: {
    readonly provider: string;
    readonly displayName: string;
    /**
     * Where the booking software lives.
     *
     * Absent for the internal diary, whose upstream is our own database. The
     * server requires it for every other provider rather than storing a blank
     * that would look configured.
     */
    readonly baseUrl?: string;
    readonly credentials: Readonly<Record<string, string>>;
  },
): Promise<SavedConnection> {
  return await request<SavedConnection>(session, '/v1/onboarding/connection', {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export async function previewImport(session: ApiSession): Promise<ImportPreview> {
  return await request<ImportPreview>(session, '/v1/onboarding/preview', { method: 'POST' });
}

/**
 * Applies the import.
 *
 * Sends no body on purpose. The server re-reads the booking software and
 * re-computes the plan, so what the previous screen displayed cannot become an
 * instruction — a preview is what we expect to happen, not a mandate.
 */
export async function runImport(session: ApiSession): Promise<ImportResult> {
  return await request<ImportResult>(session, '/v1/onboarding/import', { method: 'POST' });
}

/* ---------------------------------------------------------- invitations -- */

/** What the server says you were given, once a token has been redeemed. */
export interface AcceptedInvitation {
  readonly tenantId: string;
  readonly role: string;
}

/**
 * Redeems an invitation.
 *
 * Two tokens, and confusing them is the whole risk: `accessToken` is who you
 * are, `invitationToken` is what you were offered. The second is a capability
 * anyone holding it could try to use, which is why the server matches the
 * address it was issued to against the address in the first — server-side,
 * with a key the browser never sees. A link forwarded to somebody else
 * therefore grants them nothing.
 *
 * No establishment header: there is no establishment yet. This call is what
 * joins one.
 */
export async function acceptInvitation(
  baseUrl: string,
  accessToken: string,
  invitationToken: string,
): Promise<AcceptedInvitation> {
  return await request<AcceptedInvitation>(
    { baseUrl, token: accessToken, tenantId: '' },
    '/v1/users/invitations/accept',
    { method: 'POST', body: JSON.stringify({ token: invitationToken }) },
  );
}

/* ---------------------------------------------------------------- go-live -- */

export type GoLiveStepKey =
  'establishment' | 'diary' | 'catalogue' | 'hours' | 'number' | 'forwarding' | 'transfer';

export type GoLiveState = 'done' | 'todo' | 'attention' | 'optional' | 'not_needed';

export interface GoLiveView {
  readonly ready: boolean;
  readonly steps: readonly {
    readonly key: GoLiveStepKey;
    readonly state: GoLiveState;
    readonly required: boolean;
  }[];
  /** The number calls are forwarded to, international form. */
  readonly number: string | null;
  /**
   * While there is no number: whether this person may take one from the
   * platform's stock, and what to do first if the salon cannot yet — pay its
   * subscription, or give the salon's postal code. Null once there is one.
   */
  readonly takeNumber: {
    readonly allowed: boolean;
    readonly blocker: 'subscription' | 'address' | null;
  } | null;
  /** The codes to type on the salon's mobile, built by the server for that number. */
  readonly forwarding: {
    readonly whenUnanswered: string;
    readonly always: string;
    readonly afterRinging: string;
    readonly cancelAll: string;
  } | null;
  readonly lastCallAt: string | null;
  readonly diary: string | null;
  readonly services: {
    readonly offered: number;
    readonly owned: number;
    readonly withoutPrice: number;
  };
}

export interface TestCallAnswer {
  readonly call: {
    readonly at: string;
    readonly answered: boolean;
    readonly failed: boolean;
    /** False when the forwarding hid the caller's number: nobody could book. */
    readonly callerPresented: boolean | null;
  } | null;
}

/** What is still missing before the telephone is answered. */
export async function getGoLive(session: ApiSession): Promise<GoLiveView> {
  return await request<GoLiveView>(session, '/v1/go-live');
}

/**
 * Takes the salon's own number from the platform's stock, in its territory.
 * Pressing again returns the same number.
 */
export async function takeNumber(
  session: ApiSession,
): Promise<{ readonly e164: string; readonly status: 'assigned' | 'existing' }> {
  return await request<{ readonly e164: string; readonly status: 'assigned' | 'existing' }>(
    session,
    '/v1/telephony/number',
    { method: 'POST' },
  );
}

/** Starts a test call; the answer is the server's clock, to ask about later. */
export async function startTestCall(session: ApiSession): Promise<{ readonly startedAt: string }> {
  return await request<{ readonly startedAt: string }>(session, '/v1/go-live/test-call', {
    method: 'POST',
  });
}

/** The first call to arrive since the test began, if any. */
export async function checkTestCall(session: ApiSession, since: string): Promise<TestCallAnswer> {
  return await request<TestCallAnswer>(
    session,
    `/v1/go-live/test-call?since=${encodeURIComponent(since)}`,
  );
}
